import type { EvidenceRecordTarget, UploadTarget } from './pwa.js';

/** Exactly one parent key — the shape every /evidence route takes (see evidence-parents.ts). */
export type EvidenceParentFields =
  | { phase_id: string }
  | { variation_id: string }
  | { compliance_item_id: string }
  | { dispute_id: string }
  | { snag_id: string }
  | { issue_id: string }
  | { defect_id: string }
  | { project_id: string };

// Written out per kind so a new parent cannot be added to the union without the compiler
// pointing here — the same reason the upload switches in pwa.ts are spelled out.
export function evidenceParentFields(target: EvidenceRecordTarget): EvidenceParentFields {
  switch (target.kind) {
    case 'phase':
      return { phase_id: target.phaseId };
    case 'variation':
      return { variation_id: target.variationId };
    case 'compliance_item':
      return { compliance_item_id: target.complianceItemId };
    case 'dispute':
      return { dispute_id: target.disputeId };
    case 'snag':
      return { snag_id: target.snagId };
    case 'issue':
      return { issue_id: target.issueId };
    case 'defect':
      return { defect_id: target.defectId };
    case 'project':
      return { project_id: target.projectId };
  }
}

/**
 * The react-query cache a target's photographs live in. A phase keeps `['evidence', phaseId]`,
 * which five screens already read; every other parent is keyed by its kind as well, so a
 * variation and a phase can never share an entry.
 */
export function evidenceQueryKey(target: UploadTarget): string[] {
  switch (target.kind) {
    case 'phase':
      return ['evidence', target.phaseId];
    // The whole visit is refetched, not a list of its own: the photographs come back on the
    // visit's detail read alongside its measurements and risks.
    case 'site_visit':
      return ['site-visit', target.siteVisitId];
    case 'variation':
      return ['evidence', 'variation', target.variationId];
    case 'compliance_item':
      return ['evidence', 'compliance_item', target.complianceItemId];
    case 'dispute':
      return ['evidence', 'dispute', target.disputeId];
    case 'snag':
      return ['evidence', 'snag', target.snagId];
    case 'issue':
      return ['evidence', 'issue', target.issueId];
    case 'defect':
      return ['evidence', 'defect', target.defectId];
    case 'project':
      return ['evidence', 'project', target.projectId];
  }
}
